import {
  Select,
  TextInput,
  Button,
  Box,
  ActionIcon,
  NumberInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import "./DynamicInputPair.scss";
import { useTaxOrganizerStore } from "../../../../../utils/stores/taxOrganizerStore";
import type { SchemaShape } from "../../../../../types";
import { validateField } from "../../../../../utils/configs/formSchema";
import { useEffect, useState } from "react";

interface DynamicInputPairProps {
  selectLabel: string;
  inputLabel: string;
  isReverse: boolean;
  selectData?: string[];
  isInputMonetary?: boolean;
  storedObjectName: string;
  groupIndex: number;
  parentObjectName: string;
  isSelectInput: boolean;
  disabled?: boolean; // Add the optional disabled prop
  errorMessages?: Record<string, string>;
}

const DynamicInputPair = ({
  selectLabel,
  inputLabel,
  isReverse,
  selectData,
  isInputMonetary,
  storedObjectName,
  groupIndex,
  parentObjectName,
  isSelectInput,
  disabled = false, // Default disabled to false
  errorMessages,
}: DynamicInputPairProps) => {
  const { responses, updateResponses, setValidationErrors } =
    useTaxOrganizerStore();

  const [buttonText, setButtonText] = useState("");

  const getItemErrorMessage = (index: number, field: string) => {
    const itemErrorKey = `${parentObjectName}[${groupIndex}].${storedObjectName}[${index}].${field}`;
    const errorMessage = errorMessages?.[itemErrorKey] ?? "";
    // Extract the last field from the error message
    const lastField = itemErrorKey.split(".").pop() ?? "";

    return errorMessage ?? `${lastField} is required`;
  };

  const getItems = () => {
    return responses[parentObjectName]?.[groupIndex]?.[storedObjectName] || [];
  };

  const itemsCount = Math.max(getItems().length, 1);

  const handleUpdate = async (index: number, field: string, value: string) => {
    const updatedParentObject = [...(responses[parentObjectName] || [])];
    if (!updatedParentObject[groupIndex]) {
      updatedParentObject[groupIndex] = {};
    }
    if (!updatedParentObject[groupIndex][storedObjectName]) {
      updatedParentObject[groupIndex][storedObjectName] = [];
    }

    const updatedItems = [...updatedParentObject[groupIndex][storedObjectName]];
    if (!updatedItems[index]) {
      updatedItems[index] = {};
    }
    updatedItems[index][field] = value;

    updatedParentObject[groupIndex][storedObjectName] = updatedItems;

    updateResponses({ [parentObjectName]: updatedParentObject });

    // Revalidate the field
    const errorMessage = await validateField(
      parentObjectName as keyof SchemaShape,
      groupIndex,
      `${storedObjectName}[${index}].${field}`,
      value,
      updatedParentObject[groupIndex]
    );

    setValidationErrors((prev) => ({
      ...prev,
      [`${parentObjectName}[${groupIndex}].${storedObjectName}[${index}].${field}`]:
        errorMessage || "",
    }));
  };

  const handleAddItem = () => {
    const updatedParentObject = [...(responses[parentObjectName] || [])];
    if (!updatedParentObject[groupIndex]) {
      updatedParentObject[groupIndex] = {};
    }
    if (!updatedParentObject[groupIndex][storedObjectName]) {
      updatedParentObject[groupIndex][storedObjectName] = [];
    }

    updatedParentObject[groupIndex][storedObjectName] = [
      ...updatedParentObject[groupIndex][storedObjectName],
      {},
    ];

    updateResponses({ [parentObjectName]: updatedParentObject });
  };

  const handleDeleteItem = (index: number) => {
    const updatedParentObject = [...(responses[parentObjectName] || [])];
    if (!updatedParentObject[groupIndex]) {
      updatedParentObject[groupIndex] = {};
    }
    if (!updatedParentObject[groupIndex][storedObjectName]) {
      updatedParentObject[groupIndex][storedObjectName] = [];
    }

    const updatedItems = [...updatedParentObject[groupIndex][storedObjectName]];
    updatedItems.splice(index, 1);
    updatedParentObject[groupIndex][storedObjectName] =
      updatedItems.length > 0 ? updatedItems : [{}];

    updateResponses({ [parentObjectName]: updatedParentObject });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia("(max-width: 460px)").matches) {
        setButtonText("Remove");
      } else {
        setButtonText("");
      }
    };

    // Set initial text
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className="dynamic-input-pair"
      style={{ flexDirection: isReverse ? "column-reverse" : "column" }}
    >
      {[...Array(itemsCount)].map((item, index) => (
        <Box
          key={`${item}-${index}`}
          className="input-pair-row"
          style={{
            border: buttonText === "" ? "none" : "1px solid #F5F5F5",
            padding: buttonText === "" ? 0 : "1rem",
            borderRadius: buttonText === "" ? 0 : "0.5rem",
            backgroundColor: buttonText === "" ? "transparent" : "#F5F5F5",
            boxShadow:
              buttonText === "" ? "none" : "0px 1px 2px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div className="input-row-input-wrapper">
            {isSelectInput ? (
              <Select
                name={`${storedObjectName}-${index}`}
                value={
                  responses[parentObjectName]?.[groupIndex]?.[
                    storedObjectName
                  ]?.[index]?.[selectLabel.toLowerCase()] ?? ""
                }
                onChange={(value) =>
                  handleUpdate(index, selectLabel.toLowerCase(), value ?? "")
                }
                required={true}
                label={selectLabel}
                data={selectData || []}
                searchable={true}
                disabled={disabled}
                error={
                  !disabled &&
                  getItemErrorMessage(index, selectLabel.toLowerCase())
                }
              />
            ) : (
              <TextInput
                name={`${storedObjectName}-${index}`}
                label={selectLabel}
                value={
                  responses[parentObjectName]?.[groupIndex]?.[
                    storedObjectName
                  ]?.[index]?.[selectLabel.toLowerCase()] ?? ""
                }
                required={true}
                onChange={(event) =>
                  handleUpdate(
                    index,
                    selectLabel.toLowerCase(),
                    event.currentTarget.value
                  )
                }
                disabled={disabled}
                error={
                  !disabled &&
                  getItemErrorMessage(index, selectLabel.toLowerCase())
                }
              />
            )}
          </div>
          <div className="input-row-input-wrapper">
            {isInputMonetary ? (
              <NumberInput
                name={`amount-${index}`}
                label={inputLabel}
                value={
                  responses[parentObjectName]?.[groupIndex]?.[
                    storedObjectName
                  ]?.[index]?.[inputLabel.toLowerCase()] ?? ""
                }
                required={true}
                leftSection={"$"}
                onChange={(value) =>
                  handleUpdate(
                    index,
                    inputLabel.toLowerCase(),
                    value.toString()
                  )
                }
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator=","
                min={0}
                disabled={disabled}
                error={
                  !disabled &&
                  getItemErrorMessage(index, inputLabel.toLowerCase())
                }
              />
            ) : (
              <TextInput
                name={`amount-${index}`}
                label={inputLabel}
                value={
                  responses[parentObjectName]?.[groupIndex]?.[
                    storedObjectName
                  ]?.[index]?.[inputLabel.toLowerCase()] ?? ""
                }
                required={true}
                onChange={(event) =>
                  handleUpdate(
                    index,
                    inputLabel.toLowerCase(),
                    event.currentTarget.value
                  )
                }
                disabled={disabled}
                error={
                  !disabled &&
                  getItemErrorMessage(index, inputLabel.toLowerCase())
                }
              />
            )}
          </div>
          {buttonText === "" ? (
            <ActionIcon
              color="red"
              variant="outline"
              onClick={() => handleDeleteItem(index)}
              disabled={itemsCount === 1 || disabled}
              className="trash"
              title={
                itemsCount === 1
                  ? "Cannot delete the last row"
                  : "Delete this item"
              }
            >
              <IconTrash size="1.125rem" />
            </ActionIcon>
          ) : (
            <Button
              variant="transparent"
              leftSection={<IconTrash />}
              color="red"
              onClick={() => handleDeleteItem(index)}
              disabled={itemsCount === 1 || disabled}
              className="trash-button"
              title={
                itemsCount === 1
                  ? "Cannot delete the last row"
                  : "Delete this item"
              }
            >
              {buttonText}
            </Button>
          )}
        </Box>
      ))}
      <Button
        className="add-item"
        variant="outline"
        leftSection={<IconPlus />}
        onClick={handleAddItem}
        disabled={disabled}
      >
        Add Item
      </Button>
    </div>
  );
};

export default DynamicInputPair;
